import { useNavigate } from 'react-router-dom';

import {
  appColors,
  Tier,
  BackrsForm,
  BackrsFormField,
  BackrsFormValues,
  onEnterKeyPress,
  SIDEBAR_COLLAPSED_WIDTH,
  AppIcon,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ImageTextBox } from '@backrs_ui/grow-ui/src/components/Header/ImageTextBox';

import {
  Box,
  SxProps,
  Theme,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { TailSpin, Style } from 'react-loader-spinner';

const GENERATING_PAGE_STRINGS = appStrings.modules.generating;
const HEADER_IMAGE = '/images/blueprint-logo-small.png';

const UNAUTHENTICATED_FORM_ENABLED = true;

const unauthenticatedForm: BackrsFormValues = {
  first_name: '',
  last_name: '',
  email: '',
  role: '',
};

const unauthenticatedFormFields: BackrsFormField[] = [
  {
    id: 'first_name',
    name: 'first_name',
    placeholder: 'First Name',
    type: 'text',
    autoComplete: 'given-name',
    autoFocus: true,
    group: 1,
    sx: {
      backgroundColor: `${appColors.neutral.mediumGray}CC`,
    },
    rules: {
      required: 'First name is required',
    },
  },
  {
    id: 'last_name',
    name: 'last_name',
    placeholder: 'Last Name',
    type: 'text',
    autoComplete: 'family-name',
    group: 1,
    sx: {
      backgroundColor: `${appColors.neutral.mediumGray}CC`,
    },
    rules: {
      required: 'Last name is required',
    },
  },
  {
    id: 'email',
    name: 'email',
    placeholder: 'Email Address',
    type: 'email',
    autoComplete: 'email',
    icon: {
      type: 'LEFT_ICON',
      leftIcon: 'Mail01',
    },
    sx: {
      backgroundColor: `${appColors.neutral.mediumGray}CC`,
      width: '100%',
    },
    rules: {
      required: 'Email is required',
      pattern: {
        value: /^\S+@\S+$/i,
        message: 'Invalid email',
      },
    },
  },
];

type GeneratingBlueprintPageProps = Readonly<{
  countSuggestedCareerPaths: number;
  tier: Tier;
  isAuthenticated: boolean;
  handleUnauthenticatedSubmit: (data: BackrsFormValues) => void;
  isSurveyPosting: boolean;
  isErrorSigningUp: boolean;
}>;

export const GeneratingBlueprintPage = ({
  countSuggestedCareerPaths,
  tier,
  isAuthenticated,
  handleUnauthenticatedSubmit,
  isSurveyPosting,
  isErrorSigningUp,
}: GeneratingBlueprintPageProps) => {
  const navigate = useNavigate();

  const methods = useForm<BackrsFormValues>({ defaultValues: unauthenticatedForm });

  const isFormValid = methods.formState.isValid;

  const handleSubmission = (data: BackrsFormValues) => {
    handleUnauthenticatedSubmit(data);
  };

  const handleNextClick = () => {
    if (!isAuthenticated) {
      // if we're not authenticated, we need to submit the form to create a new user. Once the thunk is fulfilled, we'll navigate to the next page
      methods.handleSubmit(handleSubmission)();

      return;
    }

    // if we are authenticated, we can navigate to the next page since we've already saved to the server at this point
    navigate(APP_ROUTE.MY_BLUEPRINT);
  };

  const selectRoleCustomField = (
    <Controller
      name="role"
      control={methods.control}
      rules={{ required: '', validate: (value) => value !== '' }}
      render={({ field }) => (
        <FormControl fullWidth>
          <Select
            {...field}
            displayEmpty
            IconComponent={(props) => (
              <Box sx={chevronIconContainerSx(props.className.includes('MuiSelect-iconOpen'))}>
                <AppIcon
                  iconName="ChevronDown"
                  diameter="16px"
                  color="Colors/Text/text-placeholder"
                />
              </Box>
            )}
            renderValue={(selected: string | null) => {
              if (!selected) {
                return (
                  <Typography color={({ backrs }) => backrs.colors['Colors/Text/text-placeholder']}>
                    {GENERATING_PAGE_STRINGS.role.whatBestDescribesYou}
                  </Typography>
                );
              }

              return selected.charAt(0).toUpperCase() + selected.slice(1);
            }}
            sx={selectRoleSx}
          >
            <MenuItem value="default" disabled sx={menuItemSx}>
              {GENERATING_PAGE_STRINGS.role.whatBestDescribesYou}
            </MenuItem>
            <MenuItem value="student">{GENERATING_PAGE_STRINGS.role.student}</MenuItem>
            <MenuItem value="parent">{GENERATING_PAGE_STRINGS.role.parent}</MenuItem>
            <MenuItem value="educator">{GENERATING_PAGE_STRINGS.role.educator}</MenuItem>
            <MenuItem value="employer">{GENERATING_PAGE_STRINGS.role.employer}</MenuItem>
          </Select>
        </FormControl>
      )}
    />
  );

  const bodyContent = [
    <Typography key="getting-blueprint-header" variant="body1" sx={introTextStyle} component="span">
      {GENERATING_PAGE_STRINGS.body.one}
      <span style={boldedText}>{countSuggestedCareerPaths}</span>
      {GENERATING_PAGE_STRINGS.body.two}
      <span style={boldedText}>{tier}</span>
      {GENERATING_PAGE_STRINGS.body.three}
    </Typography>,
    ...(!isAuthenticated && UNAUTHENTICATED_FORM_ENABLED
      ? [
          <BackrsForm
            key="new-user-register-form"
            fields={unauthenticatedFormFields}
            methods={methods}
            onSubmit={handleSubmission}
          >
            {selectRoleCustomField}
          </BackrsForm>,
        ]
      : []),
    ...(isErrorSigningUp
      ? [
          <Typography key="error-message" variant="body1" color="error">
            {GENERATING_PAGE_STRINGS.error}
          </Typography>,
        ]
      : []),
  ];

  const isButtonDisabled =
    isSurveyPosting || (!isFormValid && !isAuthenticated && UNAUTHENTICATED_FORM_ENABLED);

  return (
    <Box
      sx={containerStyle}
      tabIndex={0}
      onKeyDown={(event) => onEnterKeyPress(event, handleNextClick)}
    >
      <ImageTextBox body={bodyContent} imageSrc={HEADER_IMAGE} />

      <Button
        variant="contained"
        sx={buttonStyle}
        onClick={handleNextClick}
        disabled={isButtonDisabled}
      >
        {/* To ensure the button doesn't change shape, we just set the opacity of the text to 0 when the absolute positioned loading spinner is shown */}
        {isSurveyPosting ? (
          <TailSpin
            color="#000"
            height={28}
            width={28}
            strokeWidth={3}
            wrapperStyle={surveyPostingLoadingIconStyle}
          />
        ) : null}

        <Typography
          sx={{
            ...buttonTextStyle,
            opacity: isSurveyPosting ? 0 : 1,
            color: isButtonDisabled ? appColors.neutral.lightGray : appColors.neutral.white,
          }}
        >
          {GENERATING_PAGE_STRINGS.actionButton}
        </Typography>
      </Button>

      {/* SHOW LOGIN OPTION FOR UNAUTHENTICATED USERS THAT HAVE AN ACCOUNT BUT SKIPPED SIGNING IN */}
      {!isAuthenticated && (
        <Typography variant="body1" mt={2}>
          {GENERATING_PAGE_STRINGS.unauthenticatedFooter.one}
          <Typography
            component="a"
            onClick={() => navigate(APP_ROUTE.LOGIN)}
            sx={unauthenticatedFooterStyle}
          >
            {GENERATING_PAGE_STRINGS.unauthenticatedFooter.two}
          </Typography>
          {GENERATING_PAGE_STRINGS.unauthenticatedFooter.three}
        </Typography>
      )}
    </Box>
  );
};

const boldedText: React.CSSProperties = {
  fontWeight: 'bold',
};

const introTextStyle: SxProps<Theme> = {
  whiteSpace: 'pre-line',
} as const;

const containerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flexGrow: 1,
  width: { xs: 'unset', sm: '100%' },
  minHeight: '100svh',
  pl: `${SIDEBAR_COLLAPSED_WIDTH}px`,
  mx: 2,
  boxSizing: 'border-box',
} as const;

const buttonStyle: SxProps<Theme> = {
  marginTop: '3rem',
  borderRadius: '2rem',
  backgroundColor: appColors.color.blue1,
  paddingX: { xs: 4, sm: '4rem' },
  border: `1px solid ${appColors.neutral.transparent}`,
  '&:disabled': {
    backgroundColor: appColors.neutral.mediumGray,
    border: `1px solid ${appColors.neutral.lightGray}`,
  },
} as const;

const buttonTextStyle: SxProps<Theme> = {
  textTransform: 'none',
  fontWeight: 'bold',
  fontSize: 20,
  color: appColors.neutral.white,
  userSelect: 'none',
} as const;

const unauthenticatedFooterStyle: SxProps<Theme> = {
  color: appColors.color.blue1,
  fontWeight: 'bold',
  cursor: 'pointer',
  textDecoration: 'none',
} as const;

const surveyPostingLoadingIconStyle: Style = {
  paddingTop: '4px',
  paddingBottom: '4px',
  position: 'absolute',
} as const;

const chevronIconContainerSx = (isOpen: boolean): SxProps<Theme> => ({
  mr: 1,
  transition: 'all 0.4s ease-in-out',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
});

const selectRoleSx: SxProps<Theme> = {
  width: '100%',
  mt: 1,
  pl: 0.5,
  backgroundColor: 'white',
  borderColor: appColors.neutral.lightGray,
  color: appColors.neutral.gray3,
  '&:hover': {
    borderColor: appColors.neutral.lightGray,
    boxShadow: 'none',
  },
  '&:focus': {
    borderColor: appColors.neutral.lightGray,
    boxShadow: 'none',
  },
  '&:active': {
    borderColor: appColors.neutral.lightGray,
    boxShadow: 'none',
  },
  '&.Mui-focused': {
    outline: 'none',
    boxShadow: 'none',
    borderColor: appColors.neutral.lightGray,
  },
  '&.Mui-active': {
    borderColor: appColors.neutral.lightGray,
  },
};

const menuItemSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-placeholder-hover'],
});
